import { formatDatabaseSizeBytes } from '@app/pages/admin/orchestration-database-connections.util';
import { AssignmentDataRecordGroup } from '@app/pages/admin/orchestration-data-records.util';
import { orchestrationSatelliteProductUrl } from '@app/pages/admin/orchestration-identity.util';

const AUDIT_KIND = 'audit';
const FILE_STORAGE_TABLE = 'FileStorageItems';
const SATELLITE_PRODUCTS_TABLE = 'SatelliteProducts';

/** One object this assignment wrote to object storage. */
export interface AssignmentOverviewFile {
  id: string;
  bucket: string;
  path: string;
  bytesLabel: string;
  downloadUrl: string | null;
}

/** Catalogue product id linked from the overview artifact list. */
export interface AssignmentOverviewProductLink {
  id: string;
  url: string;
}

/** One non-audit database table this assignment wrote, without repeating file rows. */
export interface AssignmentOverviewTable {
  key: string;
  table: string;
  count: number;
  bytesLabel: string;
  products: AssignmentOverviewProductLink[];
}

/** Follow-up assignment started because this run published an event. */
export interface AssignmentFollowUp {
  eventId: string;
  eventKey: string;
  eventUrl: string | null;
  state: string;
  targetAssignmentId: string;
  targetAssignmentUrl: string | null;
}

export function assignmentOverviewFiles(
  rows: ReadonlyArray<{
    id: string;
    path: string | null;
    bucket: string;
    bytesLabel: string;
    downloadUrl: string | null;
  }>,
): AssignmentOverviewFile[] {
  return rows.map(row => ({
    id: row.id,
    bucket: row.bucket,
    path: row.path?.trim() || row.id,
    bytesLabel: row.bytesLabel,
    downloadUrl: row.downloadUrl,
  }));
}

export function assignmentOverviewTables(
  groups: ReadonlyArray<AssignmentDataRecordGroup>,
): AssignmentOverviewTable[] {
  return groups
    .filter(group =>
      group.kind !== AUDIT_KIND
      && group.table !== FILE_STORAGE_TABLE
      && group.count > 0)
    .map(group => ({
      key: `${group.database}:${group.table}`,
      table: group.table,
      count: group.count,
      bytesLabel: formatDatabaseSizeBytes(group.bytesWritten),
      products: group.table === SATELLITE_PRODUCTS_TABLE
        ? group.items.flatMap(item => {
          const id = item.id.trim();
          const url = orchestrationSatelliteProductUrl(id);
          return id && url ? [{ id, url }] : [];
        })
        : [],
    }));
}

export function assignmentFollowUps(
  events: ReadonlyArray<{
    id: string;
    eventKey: string;
    eventUrl: string | null;
    state: string;
    targetAssignmentId: string | null;
    targetAssignmentUrl: string | null;
  }>,
): AssignmentFollowUp[] {
  return events.flatMap(event => {
    const targetAssignmentId = event.targetAssignmentId?.trim();
    if (!targetAssignmentId) {
      return [];
    }
    return [{
      eventId: event.id,
      eventKey: event.eventKey,
      eventUrl: event.eventUrl,
      state: event.state,
      targetAssignmentId,
      targetAssignmentUrl: event.targetAssignmentUrl,
    }];
  });
}
